import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Home as HomeIcon, LogOut, Building2, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { siteConfig } from '@/config/siteConfig';
import { usePortfolioAuth } from '@/context/PortfolioAuthContext';
import { toast } from 'sonner';

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = usePortfolioAuth();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
    { path: '/faq', label: 'FAQ' },
  ];

  // Only show Portfolio App link if not authenticated
  if (!isAuthenticated()) {
    navLinks.push({ 
      path: '/portfolio/login', 
      label: 'PortFolio App',
      isHighlighted: true
    });
  }

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <HomeIcon className="h-8 w-8 text-[#1e3a8a]" />
            <div>
              <h1 className="text-xl font-bold text-[#1e3a8a]">{siteConfig.agentName}</h1>
              <p className="text-xs text-slate-600">{siteConfig.tagline}</p>
              <p className="text-[10px] text-slate-500">{siteConfig.tagline2}</p>
              {/* <p className="text-[10px] text-slate-500">&copy; {new Date().getFullYear()} {siteConfig.agentName}. All rights reserved.</p> */}
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors ${
                  link.isHighlighted 
                    ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white px-4 py-2 rounded-lg shadow-md hover:shadow-lg hover:from-blue-700 hover:to-blue-900' 
                    : isActive(link.path) 
                      ? 'text-[#1e3a8a] border-b-2 border-[#d4af37] pb-1' 
                      : 'text-slate-600 hover:text-[#1e3a8a]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {isAuthenticated() ? (
              <>
                <Link to="/chat-agent">
                  <Button
                    variant="outline"
                    className="border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white px-6"
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Add Property
                  </Button>
                </Link>
                <Link to="/portfolio">
                  <Button
                    variant="outline"
                    className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white px-6"
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    My Portfolio
                  </Button>
                </Link>
                {/* <Link to="/portfolio/dashboard">
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                      <rect width="7" height="9" x="3" y="3" rx="1" />
                      <rect width="7" height="5" x="14" y="3" rx="1" />
                      <rect width="7" height="9" x="14" y="12" rx="1" />
                      <rect width="7" height="5" x="3" y="16" rx="1" />
                    </svg>
                    Dashboard
                  </Button>
                </Link> */}
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="border-red-500 text-red-600 hover:bg-red-500 hover:text-white px-6"
                  data-testid="nav-logout-btn"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
                <span className="text-xs text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                  {user?.name}
                </span>
              </>
            ) : (
              <Link to="/signup">
                <Button
                  data-testid="nav-signup-btn"
                  className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white px-6"
                >
                  Get Started
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pb-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-[#1e3a8a] text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {isAuthenticated() ? (
              <div className="space-y-2">
                <Link to="/chat-agent" onClick={() => setIsOpen(false)}>
                  <Button className="w-full border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white">
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Add Property (Chat)
                  </Button>
                </Link>
                <Link to="/portfolio" onClick={() => setIsOpen(false)}>
                  <Button className="w-full border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white">
                    <Building2 className="mr-2 h-4 w-4" />
                    My Portfolio
                  </Button>
                </Link>
                <div className="px-4 py-2 bg-slate-100 rounded-lg">
                  <p className="text-xs text-slate-600">Signed in as:</p>
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                </div>
                <Button
                  onClick={() => {
                    setIsOpen(false);
                    handleLogout();
                  }}
                  className="w-full border-red-500 text-red-600 hover:bg-red-500 hover:text-white"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </div>
            ) : (
              <Link to="/signup" onClick={() => setIsOpen(false)}>
                <Button
                  data-testid="mobile-nav-signup-btn"
                  className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
                >
                  Get Started
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
